import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/contexts/I18nContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck,
  Mail,
  Scale,
  FileText,
  Cookie,
  Send,
  Loader2,
  CheckCircle2,
  Building2,
} from "lucide-react";

const AUTHORITIES = [
  {
    code: "it",
    flag: "🇮🇹",
    name: "Garante per la Protezione dei Dati Personali",
    url: "https://www.garanteprivacy.it",
    country: "Italy",
  },
  {
    code: "pl",
    flag: "🇵🇱",
    name: "Urząd Ochrony Danych Osobowych (UODO)",
    url: "https://uodo.gov.pl",
    country: "Poland",
  },
  {
    code: "eu",
    flag: "🇪🇺",
    name: "European Data Protection Board (EDPB)",
    url: "https://edpb.europa.eu",
    country: "European Union",
  },
];

const REQUEST_TYPES = ["access", "portability", "rectification", "erasure", "objection", "other"];

export default function PrivacyCenter() {
  const { t, locale } = useI18n();
  const [email, setEmail] = useState("");
  const [requestType, setRequestType] = useState("access");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !message.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/public/privacy-request", {
        email,
        request_type: requestType,
        message: message.trim(),
        locale,
      });
      setSubmitted(true);
      toast.success(t("privacy_center.submit_success"));
    } catch (err) {
      toast.error(t("privacy_center.submit_failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <section className="relative overflow-hidden border-b border-slate-100 bg-gradient-to-b from-[#9ACD32]/10 via-white to-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-10 lg:py-24">
          <Link to="/" className="text-xs font-bold uppercase tracking-[0.2em] text-[#7AAA20] hover:text-[#9ACD32]" data-testid="privacy-center-back">
            {t("legal.back_home")}
          </Link>
          <div className="mt-5 flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#0B0E5C]/5 text-[#0B0E5C]">
              <ShieldCheck className="h-6 w-6" />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#7AAA20]">
              {t("privacy_center.pretitle")}
            </span>
          </div>
          <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-[#0B0E5C] sm:text-4xl lg:text-5xl">
            {t("privacy_center.title")}
          </h1>
          <p className="mt-4 max-w-3xl text-base text-slate-600 sm:text-lg">
            {t("privacy_center.subtitle")}
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-10 lg:py-16">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* DPO contact card */}
          <Card className="border-slate-200 p-6" data-testid="privacy-center-dpo-card">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#9ACD32]/15 text-[#0B0E5C]">
                <Mail className="h-5 w-5" />
              </span>
              <h2 className="font-display text-lg font-bold text-[#0B0E5C]">{t("privacy_center.dpo_title")}</h2>
            </div>
            <p className="mt-3 text-sm text-slate-600">{t("privacy_center.dpo_desc")}</p>
            <a
              href="mailto:privacy@telcavoip.eu"
              className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0B0E5C] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#0F1370]"
              data-testid="privacy-center-dpo-email"
            >
              <Mail className="h-4 w-4" />
              privacy@telcavoip.eu
            </a>
          </Card>

          {/* Controller card */}
          <Card className="border-slate-200 p-6" data-testid="privacy-center-controller-card">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#9ACD32]/15 text-[#0B0E5C]">
                <Building2 className="h-5 w-5" />
              </span>
              <h2 className="font-display text-lg font-bold text-[#0B0E5C]">{t("privacy_center.controller_title")}</h2>
            </div>
            <p className="mt-3 text-sm text-slate-600">{t("privacy_center.controller_desc")}</p>
            <div className="mt-4 rounded-lg bg-slate-50 p-4 text-sm text-slate-700">
              <p className="font-semibold text-[#0B0E5C]">TelcaVoIP International EU</p>
              <p className="mt-1">Ul. Zbozowa 4A, 70-653 Szczecin, Poland</p>
              <p className="font-mono">VAT: PL8513242072</p>
              <p className="mt-2 text-xs text-slate-500">Tel. IT: +39 0427 881500 · PL: +48 573 187804</p>
            </div>
          </Card>

          {/* Documents card */}
          <Card className="border-slate-200 p-6" data-testid="privacy-center-docs-card">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#9ACD32]/15 text-[#0B0E5C]">
                <FileText className="h-5 w-5" />
              </span>
              <h2 className="font-display text-lg font-bold text-[#0B0E5C]">{t("privacy_center.docs_title")}</h2>
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link to="/privacy" className="inline-flex items-center gap-2 text-[#0B0E5C] hover:text-[#7AAA20]" data-testid="privacy-center-privacy-link">
                  <FileText className="h-4 w-4" />
                  {t("privacy_center.link_privacy")}
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="inline-flex items-center gap-2 text-[#0B0E5C] hover:text-[#7AAA20]" data-testid="privacy-center-cookies-link">
                  <Cookie className="h-4 w-4" />
                  {t("privacy_center.link_cookies")}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="inline-flex items-center gap-2 text-[#0B0E5C] hover:text-[#7AAA20]" data-testid="privacy-center-terms-link">
                  <FileText className="h-4 w-4" />
                  {t("privacy_center.link_terms")}
                </Link>
              </li>
            </ul>
          </Card>
        </div>

        {/* Data request form */}
        <Card className="mt-10 border-slate-200 p-6 sm:p-8" data-testid="privacy-center-form-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#9ACD32]/15 text-[#0B0E5C]">
              <Send className="h-5 w-5" />
            </span>
            <h2 className="font-display text-xl font-bold text-[#0B0E5C]">{t("privacy_center.form_title")}</h2>
          </div>
          <p className="mt-3 max-w-2xl text-sm text-slate-600">{t("privacy_center.form_desc")}</p>

          {submitted ? (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-[#9ACD32]/40 bg-[#9ACD32]/5 p-5" data-testid="privacy-center-form-success">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#7AAA20]" />
              <div>
                <p className="font-semibold text-[#0B0E5C]">{t("privacy_center.success_title")}</p>
                <p className="mt-1 text-sm text-slate-600">{t("privacy_center.success_desc")}</p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t("privacy_center.field_email")}
                  </label>
                  <Input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-1.5"
                    data-testid="privacy-center-email-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    {t("privacy_center.field_type")}
                  </label>
                  <Select value={requestType} onValueChange={setRequestType}>
                    <SelectTrigger className="mt-1.5" data-testid="privacy-center-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REQUEST_TYPES.map((rt) => (
                        <SelectItem key={rt} value={rt} data-testid={`privacy-center-type-${rt}`}>
                          {t(`privacy_center.type_${rt}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  {t("privacy_center.field_message")}
                </label>
                <Textarea
                  required
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  placeholder={t("privacy_center.field_message_placeholder")}
                  className="mt-1.5"
                  data-testid="privacy-center-message-input"
                />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                <p className="text-xs text-slate-500">{t("privacy_center.deadline_note")}</p>
                <Button
                  type="submit"
                  disabled={submitting || !email || !message.trim()}
                  className="rounded-full bg-[#0B0E5C] font-bold text-white hover:bg-[#0F1370]"
                  data-testid="privacy-center-submit-btn"
                >
                  {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  {t("privacy_center.submit_btn")}
                </Button>
              </div>
            </form>
          )}
        </Card>

        {/* Supervisory authorities */}
        <section className="mt-12" data-testid="privacy-center-authorities">
          <div className="flex items-center gap-3">
            <Scale className="h-5 w-5 text-[#0B0E5C]" />
            <h2 className="font-display text-xl font-bold text-[#0B0E5C]">{t("privacy_center.authorities_title")}</h2>
          </div>
          <p className="mt-2 max-w-3xl text-sm text-slate-600">{t("privacy_center.authorities_desc")}</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {AUTHORITIES.map((a) => (
              <a
                key={a.code}
                href={a.url}
                target="_blank"
                rel="noreferrer noopener"
                className="group flex items-start gap-3 rounded-xl border border-slate-200 p-4 transition-colors hover:border-[#9ACD32]/60 hover:bg-[#9ACD32]/5"
                data-testid={`privacy-center-authority-${a.code}`}
              >
                <span className="text-2xl" aria-hidden>{a.flag}</span>
                <div className="flex-1 text-sm">
                  <p className="font-semibold text-[#0B0E5C] group-hover:text-[#7AAA20]">{a.name}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{a.country}</p>
                  <p className="mt-1 text-xs text-slate-400">{a.url.replace(/^https?:\/\//, "")}</p>
                </div>
              </a>
            ))}
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
